'use client';

import Image from 'next/image';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { type NavEntry, type SportsbookNavigationMenuProps, PageType } from './types';
import { filterAndSortEntries, findTargetEntry, performNavigation } from './utils';

// Keep in mind that this is not the final version of the navigation menu,
// it is a temporary solution to get the navigation menu working.

function SportsbookNavigationMenu({ entries, isUserLoggedIn = false, onNavigate, className = '' }: SportsbookNavigationMenuProps) {
  const filteredEntries = useMemo(() => filterAndSortEntries(entries || [], isUserLoggedIn), [entries, isUserLoggedIn]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [hasPerformedInitialNavigation, setHasPerformedInitialNavigation] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || !filteredEntries.length || hasPerformedInitialNavigation) return;

    const urlParams = new URLSearchParams(window.location.search);
    const pageParam = urlParams.get('page');

    if (pageParam) {
      const targetEntry = findTargetEntry(filteredEntries, pageParam);

      if (targetEntry) {
        const targetIndex = filteredEntries.indexOf(targetEntry);
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setActiveIndex(targetIndex);

        setHasPerformedInitialNavigation(true);
      }
    } else {
      setHasPerformedInitialNavigation(true);
    }
  }, [filteredEntries, hasPerformedInitialNavigation]);

  const handleMenuClick = useCallback(
    (entry: NavEntry, index: number) => {
      setActiveIndex(index);

      if (onNavigate) {
        onNavigate(entry);
      }

      switch (entry.pageType) {
        case PageType.Page:
        case PageType.IDs:
          performNavigation(entry);
          break;

        case PageType.Url:
          break;
      }
    },
    [onNavigate]
  );

  if (!filteredEntries.length) {
    return (
      <div className={`bg-background border-b border-border ${className}`}>
        <div className="flex justify-center py-4">
          <div className="text-foreground-subtle paragraph-s">Loading navigation...</div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-background border-b border-border ${className}`}>
      <div className="flex justify-center">
        <div className="flex">
          {filteredEntries.map((entry, index) => {
            const isActive = index === activeIndex;
            const isExternalUrl = entry.pageType === PageType.Url || (entry.url && entry.url.startsWith('http'));

            const linkProps = isExternalUrl
              ? {
                  href: entry.url,
                  target: entry.openInNewTab ? '_blank' : '_self',
                  rel: entry.openInNewTab ? 'noopener noreferrer' : undefined,
                }
              : {
                  href: '#',
                  onClick: (e: React.MouseEvent) => {
                    e.preventDefault();
                    handleMenuClick(entry, index);
                  },
                };

            return (
              <a
                key={entry.id}
                {...linkProps}
                className={`
                  group relative flex flex-col items-center text-center text-decoration-none
                  min-w-[137px] h-[65px] px-4
                  hover:cursor-pointer focus:outline-none hover:bg-container-hover
                  transition-all duration-250 ease-in-out
                  ${isActive ? 'paragraph-strong' : 'paragraph'}
                `}
              >
                <div className="mt-3 mb-2">
                  <Image src={entry.icon.url} alt={entry.icon.title} width={24} height={24} className="w-6 h-6 text-foreground" />
                </div>

                <span
                  className={`
                  paragraph-s uppercase text-foreground
                  text-center px-1
                  ${isActive ? 'paragraph-s-strong' : 'paragraph-s'}
                  group-hover:paragraph-s-strong
                `}
                >
                  {entry.title}
                </span>

                <div
                  className={`
                  absolute bottom-0 left-0 right-0
                  h-1 bg-accent-green transition-all duration-250 ease-in-out
                  ${isActive ? 'w-full' : 'w-0 group-hover:w-full'}
                `}
                />

                {index < filteredEntries.length - 1 && <div className="absolute right-0 top-1/2 transform -translate-y-1/2 w-px h-8 bg-border" />}
              </a>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export { SportsbookNavigationMenu };
